// Same-origin JSON API client (CSRF header on mutating requests, French errors, 401 hook) and the
// Server-Sent Events connection with reconnect + status callback. Writes also carry this page's
// build id (X-Office-Build); the server's own, read from every response, feeds the update notice.
import { t } from './i18n/fr.js';
import { CLIENT_BUILD } from './version.js';

/** Error thrown by every API call. status 0 = network error or timeout. */
export class ApiError extends Error {
  /**
   * @param {number} status HTTP status (0 when the request did not complete)
   * @param {string} message French message (server `error` field or a generic one)
   * @param {object} [extra] { details, data, retryAfter (seconds), path }
   */
  constructor(status, message, extra = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = extra.details ?? null;
    this.data = extra.data ?? null;
    this.retryAfter = extra.retryAfter ?? null;
    this.path = extra.path ?? '';
  }

  get notFound() { return this.status === 404; }
  get unauthorized() { return this.status === 401; }
  get forbidden() { return this.status === 403; }
  get network() { return this.status === 0; }
  /** A write refused because this page is older than the build served (see isStaleClient). */
  get staleClient() { return isStaleClient(this); }
}

/**
 * True for the 409 a write gets when this page is older than the build the server serves
 * (`{error, code: 'staleClient'}`). It is no conflict on the data: show it like any other error,
 * keep what the person typed (the shell shows the « nouvelle version » banner).
 * @param {unknown} err
 */
export function isStaleClient(err) {
  return err?.status === 409 && err?.data?.code === 'staleClient';
}

/**
 * True for a 409 about the data itself (a task that changed state, a reply already running, a
 * name already taken…), the one a panel handles in its own way; never a staleClient refusal.
 * @param {unknown} err
 */
export function isConflict(err) {
  return err?.status === 409 && !isStaleClient(err);
}

const state = {
  csrf: '', unauthorized: new Set(), fetchImpl: null,
  clientBuild: CLIENT_BUILD, // sent with every write (null: not sent)
  serverBuild: new Set(),    // onServerBuild listeners
  staleClient: new Set(),    // onStaleClient listeners
  writes: 0,                 // writes in flight (pendingWrites)
};
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
// Endpoints whose 401 is an expected answer (wrong password…) rather than a lost session.
const AUTH_PATHS = /^\/api\/auth\/(login|setup|me)\b/;

/** Stores the CSRF token returned by /api/auth/me, /api/auth/login or /api/bootstrap. */
export function setCsrf(token) {
  state.csrf = typeof token === 'string' ? token : '';
}

/** The current CSRF token. */
export function getCsrf() {
  return state.csrf;
}

/**
 * Registers a callback for a lost session (401 on a non-auth endpoint).
 * @returns {() => void} unsubscribe
 */
export function onUnauthorized(fn) {
  state.unauthorized.add(fn);
  return () => state.unauthorized.delete(fn);
}

/** Test hook: replaces fetch (null restores the global one). */
export function setFetch(fn) {
  state.fetchImpl = fn;
}

/** Test hook: the build id sent in X-Office-Build (null: none; the default is CLIENT_BUILD). */
export function setClientBuild(id) {
  state.clientBuild = typeof id === 'string' && id ? id : null;
}

/**
 * Registers a callback for the build the server serves (`X-Office-Build` of any API response).
 * @param {(build: string) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onServerBuild(fn) {
  state.serverBuild.add(fn);
  return () => state.serverBuild.delete(fn);
}

/**
 * Registers a callback for a write refused because this page is older than the build served
 * (409 `code: 'staleClient'`; the caller still gets the ApiError, shown like any other: panels
 * test `isConflict(err)`, not `status === 409`, before treating a 409 as a conflict).
 * @param {(err: ApiError) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onStaleClient(fn) {
  state.staleClient.add(fn);
  return () => state.staleClient.delete(fn);
}

/** Writes (POST/PUT/PATCH/DELETE, uploads included) sent and not answered yet. */
export function pendingWrites() {
  return state.writes;
}

function notify(listeners, value) {
  for (const fn of [...listeners]) {
    try { fn(value); } catch (e) { console.error(e); }
  }
}

/**
 * Appends query parameters to a path, skipping null/undefined/'' values.
 * @param {string} path
 * @param {Record<string, any>} [params]
 */
export function withQuery(path, params = {}) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `${path}${path.includes('?') ? '&' : '?'}${s}` : path;
}

function fallbackMessage(status) {
  const key = `api.${status}`;
  const msg = t(key);
  if (msg !== key) return msg;
  if (status >= 500) return t('api.500');
  return t('api.generic', { status });
}

function parseRetryAfter(body, headers) {
  const fromBody = Number(body?.retryAfter);
  if (Number.isFinite(fromBody) && fromBody > 0) return fromBody;
  const h = Number(headers?.get?.('retry-after'));
  return Number.isFinite(h) && h > 0 ? h : null;
}

/**
 * True when a path could be rewritten by the browser's URL normalisation: a "." or ".." segment
 * (also percent-encoded) or a backslash. Ids come from the location hash (a link anyone can send),
 * so `/api/tasks/${encodeURIComponent('..')}/cancel` must never become `/api/cancel`.
 * @param {string} path
 */
export function isUnsafePath(path) {
  const p = String(path).split(/[?#]/)[0];
  if (p.includes('\\')) return true;
  return p.split('/').some((seg) => {
    const s = seg.replace(/%2e/gi, '.');
    return s === '.' || s === '..';
  });
}

/**
 * Performs a JSON request against the same origin.
 * @param {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'} method
 * @param {string} path must start with "/" (e.g. "/api/tasks")
 * @param {any} [body] JSON-serialisable body (omitted when undefined); with `raw`, sent as is (a
 *   File or Blob: an upload)
 * @param {{signal?: AbortSignal, timeoutMs?: number, headers?: object, raw?: boolean, contentType?: string}} [opts]
 * @returns {Promise<any>} parsed JSON, or null for 204 / empty responses
 * @throws {ApiError}
 */
export async function request(method, path, body, opts = {}) {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || isUnsafePath(path)) {
    throw new ApiError(0, t('api.400'), { path });
  }
  const fetchFn = state.fetchImpl || globalThis.fetch;
  const headers = { Accept: 'application/json', ...(opts.headers || {}) };
  const init = { method, headers, credentials: 'same-origin', cache: 'no-store', redirect: 'error' };
  if (body !== undefined && opts.raw) {
    headers['Content-Type'] = opts.contentType || body?.type || 'application/octet-stream';
    init.body = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const write = MUTATING.has(method);
  if (write && state.csrf) headers['X-CSRF-Token'] = state.csrf;
  // The server refuses a write from a page older than its build (409 staleClient).
  if (write && state.clientBuild) headers['X-Office-Build'] = state.clientBuild;

  if (write) state.writes += 1;
  try {
    return await send(fetchFn, path, init, opts);
  } finally {
    if (write) state.writes -= 1;
  }
}

/** Sends the request built by request() and reads the answer (timeout, errors, hooks). */
async function send(fetchFn, path, init, opts) {
  const ctrl = new AbortController();
  const timeoutMs = opts.timeoutMs ?? 30000;
  let timedOut = false;
  const timer = timeoutMs > 0 ? setTimeout(() => { timedOut = true; ctrl.abort(); }, timeoutMs) : null;
  const onAbort = () => ctrl.abort();
  opts.signal?.addEventListener?.('abort', onAbort, { once: true });
  init.signal = ctrl.signal;

  let res;
  try {
    res = await fetchFn(path, init);
  } catch (err) {
    if (opts.signal?.aborted) throw err;
    throw new ApiError(0, timedOut ? t('api.timeout') : t('api.network'), { path });
  } finally {
    if (timer) clearTimeout(timer);
    opts.signal?.removeEventListener?.('abort', onAbort);
  }
  const build = res.headers?.get?.('x-office-build');
  if (build) notify(state.serverBuild, build);

  let data = null;
  if (res.status !== 204) {
    const type = res.headers?.get?.('content-type') || '';
    try {
      if (type.includes('application/json')) data = await res.json();
      else await res.text();
    } catch {
      data = null;
    }
  }
  if (res.ok) return data;

  const message = (data && typeof data.error === 'string' && data.error) || fallbackMessage(res.status);
  const err = new ApiError(res.status, message, {
    details: data?.details ?? null, data, retryAfter: parseRetryAfter(data, res.headers), path,
  });
  if (res.status === 401 && !AUTH_PATHS.test(path)) notify(state.unauthorized, err);
  if (err.staleClient) notify(state.staleClient, err);
  throw err;
}

/** The API client used everywhere (and passed to panels as ctx.api). */
export const api = {
  get: (path, opts) => request('GET', path, undefined, opts),
  post: (path, body, opts) => request('POST', path, body, opts),
  put: (path, body, opts) => request('PUT', path, body, opts),
  patch: (path, body, opts) => request('PATCH', path, body, opts),
  del: (path, opts) => request('DELETE', path, undefined, opts),
  /** POST a File / Blob as the request body (CSRF header like any write); 10 minutes to send it. */
  upload: (path, blob, opts = {}) => request('POST', path, blob, { timeoutMs: 600000, ...opts, raw: true }),
  request,
  withQuery,
};

/** SSE event types the app listens to (server `event:` names). */
export const EVENT_TYPES = [
  'hello', 'task', 'task.removed', 'agent', 'office', 'notify', 'connectors', 'counts', 'stats',
  'routine', 'routine.removed', 'trigger', 'trigger.removed', 'calendar', 'chat', 'atelier', 'usage',
  // Tables du bureau changed (admins only; sent only when the office's MCP server is on).
  'tables',
  // Improvement suggestions (their author and the admins).
  'suggestion', 'suggestion.removed',
  // The office's connector library (every user; never an address, the tools nor a login).
  'connector-library', 'connector-library.removed',
  // An engine's state changed (every user; only {checkedAt}: the page reads GET /api/engines/status).
  'engines',
  // A studio element changed (the users of its service), the « À régler » alerts changed.
  'studio', 'notices',
];

const BACKOFF = [1000, 2000, 5000, 10000, 30000];

/**
 * Opens the SSE stream (/api/events) and keeps it alive.
 * Status values: 'connecting' → 'open'; on a transient drop 'reconnecting' (the browser retries);
 * when the stream is closed by the server (e.g. 401) 'offline' then a new attempt with backoff;
 * 'closed' after close(); 'unsupported' without EventSource.
 * @param {(type: string, data: any) => void} onEvent
 * @param {{onStatus?: (status: string) => void, url?: string, types?: string[],
 *   EventSourceImpl?: Function, backoff?: number[]}} [opts]
 * @returns {{close(): void, reconnect(): void, addType(type: string): void, readonly status: string}}
 */
export function connectEvents(onEvent, opts = {}) {
  const url = opts.url || '/api/events';
  const types = new Set(opts.types || EVENT_TYPES);
  const backoff = opts.backoff || BACKOFF;
  let es = null;
  let closed = false;
  let attempt = 0;
  let timer = null;
  let status = 'idle';

  const setStatus = (s) => {
    if (s === status) return;
    status = s;
    try { opts.onStatus?.(s); } catch (err) { console.error(err); }
  };

  const listener = (type) => (ev) => {
    let data = null;
    if (ev && typeof ev.data === 'string' && ev.data !== '') {
      try { data = JSON.parse(ev.data); } catch { return; }
    }
    try { onEvent(type, data); } catch (err) { console.error('[events]', type, err); }
  };

  function attach(type) {
    es?.addEventListener(type, listener(type));
  }

  function open() {
    if (closed) return;
    const ES = opts.EventSourceImpl || globalThis.EventSource;
    if (!ES) { setStatus('unsupported'); return; }
    setStatus(attempt ? 'reconnecting' : 'connecting');
    es = new ES(url, { withCredentials: true });
    es.onopen = () => { attempt = 0; setStatus('open'); };
    es.onerror = () => {
      if (closed) return;
      if (es.readyState === 2) { // CLOSED: the browser gave up (HTTP error, 401…)
        es.close();
        es = null;
        schedule();
      } else {
        setStatus('reconnecting');
      }
    };
    for (const type of types) attach(type);
    es.onmessage = listener('message');
  }

  function schedule() {
    setStatus('offline');
    const delay = backoff[Math.min(attempt, backoff.length - 1)] + Math.floor(Math.random() * 400);
    attempt += 1;
    clearTimeout(timer);
    timer = setTimeout(open, delay);
  }

  open();
  return {
    close() {
      closed = true;
      clearTimeout(timer);
      es?.close();
      es = null;
      setStatus('closed');
    },
    reconnect() {
      if (closed) return;
      clearTimeout(timer);
      es?.close();
      es = null;
      attempt = Math.max(attempt, 1);
      open();
    },
    addType(type) {
      if (types.has(type)) return;
      types.add(type);
      attach(type);
    },
    get status() { return status; },
  };
}
